import { faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../../components/Footer/Footer';
import Menu from '../../components/Menu/Menu';
import Toast from '../../components/Toast/Toast';
import {
    completeOnboarding,
    getOnboardingStatus,
} from '../../services/OnboardingService';
import { clarityEvent } from '../../utils/clarity';
import { trackEvent } from '../../utils/googleAnalytics';
import './Onboarding.css';

// Mirrors the steps the backend computes in onboardingController.buildSteps:
// the `done` flag always comes from the API, only the copy lives here.
const STEPS = [
    {
        id: 'apiKeys',
        title: 'Connect your Binance API keys',
        description:
            'Add read and trade keys in Settings so the bot can act on your account. Never enable withdrawal permission for the bot.',
        to: '/settings',
        cta: 'Open Settings',
    },
    {
        id: 'alerts',
        title: 'Set up an alert channel',
        description:
            'Connect Telegram or your own SMTP in Settings to be notified about fills, errors and automation events.',
        to: '/settings',
        cta: 'Configure alerts',
    },
    {
        id: 'monitor',
        title: 'Create a monitor',
        description:
            'Watch prices, candles or the order book in real time and use that data to trigger your automations.',
        to: '/monitors',
        cta: 'Go to Monitors',
    },
    {
        id: 'automation',
        title: 'Create your first automation',
        description:
            'Combine a strategy with an order template so the bot can trade for you automatically.',
        to: '/automations',
        cta: 'Go to Automations',
    },
    {
        id: 'backtest',
        title: 'Run a backtest',
        description:
            'Test how an automation would have performed against historical candles before letting it trade live.',
        to: '/automations/backtests/new',
        cta: 'New backtest',
    },
];

function Onboarding() {
    const [status, setStatus] = useState(null);

    const [notification, setNotification] = useState({ type: '', text: '' });

    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getOnboardingStatus()
            .then(setStatus)
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
        trackEvent('onboarding_view');
        clarityEvent('onboarding_view');
    }, []);

    function onComplete() {
        setSaving(true);
        completeOnboarding()
            .then(result => {
                setStatus(current => ({
                    ...current,
                    onboardedAt: result.onboardedAt,
                }));
                setNotification({
                    type: 'success',
                    text: 'Getting Started marked as complete. Thanks!',
                });
                trackEvent('onboarding_complete');
                clarityEvent('onboarding_complete');
            })
            .catch(err => {
                setNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            })
            .finally(() => setSaving(false));
    }

    const doneById = {};
    (status?.steps || []).forEach(step => (doneById[step.id] = step.done));

    const doneCount = STEPS.filter(step => doneById[step.id]).length;
    const total = STEPS.length;
    const pct = Math.round((doneCount / total) * 100);

    return (
        <>
            <Menu />
            <main className="content">
                <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
                    <div className="d-block mb-4 mb-md-0">
                        <h1 className="h4">Getting Started</h1>
                        <p className="text-gray-600 mb-0">
                            Follow these steps to get the most out of
                            ArgosTraderBot. We track them for you automatically.
                        </p>
                    </div>
                </div>

                <div className="card card-body border-0 shadow mb-4">
                    <div className="d-flex justify-content-between mb-2">
                        <span className="fw-bold">
                            {doneCount} of {total} completed
                        </span>
                        <span className="text-gray-600">{pct}%</span>
                    </div>
                    <div className="progress">
                        <div
                            className="progress-bar bg-success"
                            role="progressbar"
                            style={{ width: `${pct}%` }}
                            aria-valuenow={pct}
                            aria-valuemin="0"
                            aria-valuemax="100"
                        ></div>
                    </div>

                    {status?.onboardedAt && (
                        <div className="alert alert-success d-flex align-items-center mt-4 mb-0">
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="me-2 my-vb-icon"
                            />
                            <span>
                                Onboarding completed. You can revisit this page
                                any time from the Help menu.
                            </span>
                        </div>
                    )}
                </div>

                <div className="row">
                    {STEPS.map(step => {
                        const done = !!doneById[step.id];
                        return (
                            <div className="col-12 mb-3" key={step.id}>
                                <div className="card card-body border-0 shadow step-card">
                                    <div className="d-flex align-items-start">
                                        <span
                                            className={
                                                done
                                                    ? 'step-marker step-marker-done'
                                                    : 'step-marker'
                                            }
                                        >
                                            {done ? (
                                                <FontAwesomeIcon
                                                    icon={faCheckCircle}
                                                    className="my-vb-icon"
                                                />
                                            ) : (
                                                ''
                                            )}
                                        </span>
                                        <div className="flex-grow-1 ms-3">
                                            <h5 className="mb-1">
                                                {step.title}
                                                {done && (
                                                    <span className="badge bg-success ms-2">
                                                        Done
                                                    </span>
                                                )}
                                            </h5>
                                            <p className="text-gray-600 mb-2">
                                                {step.description}
                                            </p>
                                        </div>
                                        <div className="ms-3">
                                            <Link
                                                to={step.to}
                                                className="btn btn-gray-800"
                                            >
                                                {done ? 'Review' : step.cta}
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {status?.completed && !status?.onboardedAt && (
                    <div className="d-flex justify-content-end mb-4">
                        <button
                            className="btn btn-primary"
                            onClick={onComplete}
                            disabled={saving}
                        >
                            {saving ? 'Saving...' : 'Mark as complete'}
                        </button>
                    </div>
                )}

                <Footer />
            </main>
            <Toast
                type={notification.type}
                text={notification.text}
                onShow={() => setNotification({})}
            />
        </>
    );
}

export default Onboarding;
